import type { UserOutput } from "@orsane/schema/users";
import { User, X } from "lucide-react";
import { useCallback, useMemo, useRef } from "react";
import { useUsers } from "@/queries/queries";
import { UserAvatar } from "../UserAvatar";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "../ui/command";

// Helper to compare arrays by their IDs
function arraysEqual(a: UserOutput[] | null, b: UserOutput[] | null): boolean {
    if (a === b) return true;
    if (!a || !b) return a === b;
    if (a.length !== b.length) return false;
    const aIds = a
        .map((u) => u.id)
        .sort()
        .join(",");
    const bIds = b
        .map((u) => u.id)
        .sort()
        .join(",");
    return aIds === bIds;
}

export function UserFilterPopover({
    onChange,
    value,
    showEmpty = true,
}: {
    onChange: (value: UserOutput[], closePopup: boolean) => void;
    value: UserOutput[] | null;
    showEmpty?: boolean;
}) {
    const { data } = useUsers();

    // Use ref to track previous value for deep comparison
    const prevValueRef = useRef<UserOutput[] | null>(null);
    const prevDataRef = useRef<UserOutput[] | undefined>(undefined);

    // Only recalculate if value or data actually changed (deep comparison)
    const stableValue = useMemo(() => {
        if (arraysEqual(value, prevValueRef.current)) {
            return prevValueRef.current;
        }
        prevValueRef.current = value;
        return value;
    }, [value]);

    const stableData = useMemo(() => {
        if (data === prevDataRef.current) {
            return prevDataRef.current;
        }
        prevDataRef.current = data;
        return data;
    }, [data]);

    const { selectedUsers, unselectedUsers } = useMemo(() => {
        const pt1 = stableValue || [];
        const pt2 = stableData?.filter((u) => !pt1.some((v) => v.id === u.id)) || [];

        return {
            selectedUsers: pt1,
            unselectedUsers: pt2,
        };
    }, [stableValue, stableData]);

    const handleSelect = useCallback(
        (user: UserOutput) => {
            const currentSelected = stableValue || [];
            if (currentSelected.some((u) => u.id === user.id)) {
                const newUsers = currentSelected.filter((u) => u.id !== user.id);
                onChange(newUsers, false);
            } else {
                onChange([...currentSelected, user], false);
            }
        },
        [stableValue, onChange]
    );

    return (
        <>
            <div className="p-3 flex gap-1 flex-wrap overflow-hidden border-b">
                {!stableValue?.length && (
                    <div className="leading-none grid place-items-center pl-1 text-zinc-400 py-1">
                        No users selected
                    </div>
                )}
                {selectedUsers.map((user) => {
                    return (
                        <div
                            className="flex items-center py-0 pl-1 text-sm gap-1 font-normal rounded-sm bg-zinc-200/50"
                            key={`selected_items_${user.id}`}
                        >
                            {user.id === "empty" && <User className="mx-0.5 size-4 text-zinc-600" />}
                            {user.id !== "empty" && <UserAvatar user={user} size="xs" />}
                            <div className="text-nowrap py-0.5 ml-1">{user.name}</div>
                            <button
                                type="button"
                                className="block pr-1 rounded-r-sm h-full px-0.5 appearance-none hover:text-orsane-primary hover:bg-black/10"
                                onClick={() => handleSelect(user)}
                            >
                                <X className="size-3" />
                            </button>
                        </div>
                    );
                })}
            </div>
            <Command
                filter={(_v, search, keywords) => {
                    if (keywords?.some((k) => k.includes(search))) return 1;
                    return 0;
                }}
            >
                <CommandInput autoFocus placeholder="Search for a user..." />
                <CommandList>
                    <CommandGroup>
                        {showEmpty && !stableValue?.some((u) => u.id === "empty") && (
                            <CommandItem
                                value="empty"
                                keywords={["empty"]}
                                onClick={(e) => e.stopPropagation()}
                                onSelect={() =>
                                    handleSelect({ id: "empty", name: "Empty", image: null, email: "empty" })
                                }
                            >
                                <User className="mx-0.5 size-4 text-zinc-600" />
                                Empty
                            </CommandItem>
                        )}
                        {unselectedUsers.map((user) => {
                            return (
                                <CommandItem
                                    value={user.id.toString()}
                                    key={user.id}
                                    keywords={[user.name, user.email]}
                                    onClick={(e) => e.stopPropagation()}
                                    onSelect={() => handleSelect(user)}
                                >
                                    <UserAvatar user={user} size="sm" />
                                    {user.name}
                                </CommandItem>
                            );
                        })}
                    </CommandGroup>
                </CommandList>
                <CommandEmpty>No users found.</CommandEmpty>
            </Command>
        </>
    );
}
